/**
 * Money is stored and transported as INTEGER CENTAVOS everywhere
 * (no floats). Weight is stored as INTEGER GRAMS. All authoritative
 * totals are computed server-side with these exact helpers; the web UI
 * reuses them for live previews only.
 */

/** 17500 -> "₱175.00" (deterministic, locale-independent). */
export function centsToPesos(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const pesos = Math.floor(abs / 100);
  const remainder = String(abs % 100).padStart(2, '0');
  const grouped = String(pesos).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}₱${grouped}.${remainder}`;
}

/** 49.99 -> 4999 (rounds half up; never trust float equality). */
export function pesosToCents(pesos: number): number {
  if (!Number.isFinite(pesos) || pesos < 0) {
    throw new Error(`Invalid peso amount: ${pesos}`);
  }
  return Math.round(pesos * 100);
}

/** 3500 -> "3.5" (kg display without trailing noise). */
export function gramsToKgDisplay(grams: number): string {
  const kg = grams / 1000;
  return Number.isInteger(kg) ? String(kg) : kg.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

export type ServiceUnit = 'KG' | 'PIECE' | 'FLAT';

export interface TotalAddonInput {
  unitPriceCents: number;
  quantity: number;
}

export interface TransactionTotals {
  baseCents: number;
  addonCents: number;
  totalCents: number;
}

/**
 * Authoritative total for one transaction line basis + flat add-ons.
 * - KG:   price per kg x weightGrams (rounded to the nearest centavo)
 * - PIECE: price per item x itemCount
 * - FLAT:  price once per transaction
 *
 * A flat-rate service may set `minKilos` (e.g. Light Weights: minimum 8 kg
 * for P195). The weight is still required and must reach the minimum; the
 * flat price covers the minimum load. The price of weight above the minimum
 * (the photo's "overload" charge) is not established yet, so it is not
 * calculated here.
 */
export function computeTransactionTotals(input: {
  unit: ServiceUnit;
  unitPriceCents: number;
  weightGrams?: number | null;
  itemCount: number;
  minKilos?: number | null;
  addons?: TotalAddonInput[];
}): TransactionTotals {
  const { unit, unitPriceCents, itemCount } = input;
  if (!Number.isInteger(unitPriceCents) || unitPriceCents < 0) {
    throw new Error(`Invalid unit price: ${unitPriceCents}`);
  }

  const minKilos = input.minKilos;
  if (minKilos !== undefined && minKilos !== null) {
    if (!Number.isInteger(minKilos) || minKilos <= 0) {
      throw new Error(`Invalid minimum load: ${minKilos}`);
    }
    if (unit !== 'FLAT') {
      throw new Error('Minimum-load pricing requires a flat-rate service');
    }
    const grams = input.weightGrams;
    if (!Number.isInteger(grams) || (grams ?? 0) <= 0) {
      throw new Error('Weight is required for minimum-load services');
    }
    if (grams! < minKilos * 1000) {
      throw new Error(`Minimum load is ${minKilos} kg`);
    }
  }

  let baseCents: number;
  switch (unit) {
    case 'KG': {
      const grams = input.weightGrams;
      if (!Number.isInteger(grams) || (grams ?? 0) <= 0) {
        throw new Error('Weight is required for per-kilo services');
      }
      baseCents = Math.round((unitPriceCents * grams!) / 1000);
      break;
    }
    case 'PIECE':
      if (!Number.isInteger(itemCount) || itemCount <= 0) {
        throw new Error('Item count must be positive');
      }
      baseCents = unitPriceCents * itemCount;
      break;
    case 'FLAT':
      baseCents = unitPriceCents;
      break;
    default:
      throw new Error(`Unknown service unit: ${unit}`);
  }

  const addonCents = (input.addons ?? []).reduce((sum, addon) => {
    if (!Number.isInteger(addon.unitPriceCents) || addon.unitPriceCents < 0) {
      throw new Error(`Invalid add-on price: ${addon.unitPriceCents}`);
    }
    if (!Number.isInteger(addon.quantity) || addon.quantity <= 0) {
      throw new Error(`Invalid add-on quantity: ${addon.quantity}`);
    }
    return sum + addon.unitPriceCents * addon.quantity;
  }, 0);

  return { baseCents, addonCents, totalCents: baseCents + addonCents };
}

/**
 * Derived payment status - the ONE shared source of truth for status
 * calculation (API responses, reports and UI previews all call this).
 *
 * OVERPAID is unreachable through the API (receiving rejects
 * `amountPaid > total`), so it only ever appears on inconsistent stored
 * data - reports must surface it honestly instead of hiding it as PAID.
 * A zero-total transaction with nothing paid stays UNPAID (nothing was
 * collected; nothing is outstanding either).
 */
export type PaymentStatus = 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERPAID';

export function derivePaymentStatus(amountPaidCents: number, totalCents: number): PaymentStatus {
  if (amountPaidCents > totalCents) return 'OVERPAID';
  if (amountPaidCents <= 0) return 'UNPAID';
  return amountPaidCents >= totalCents ? 'PAID' : 'PARTIAL';
}
